import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Clock, MapPin, Dumbbell, Info } from "lucide-react";
import { Link } from "react-router-dom";

const DAYS = [
  { n: 1, label: "Lundi", short: "LUN" },
  { n: 2, label: "Mardi", short: "MAR" },
  { n: 3, label: "Mercredi", short: "MER" },
  { n: 4, label: "Jeudi", short: "JEU" },
  { n: 5, label: "Vendredi", short: "VEN" },
];

// Color coding by category family
function categoryColor(cat) {
  const c = cat.toUpperCase();
  if (c.includes("U6") || c.includes("U7") || c.includes("U8") || c.includes("U9")) return { bg: "bg-[#D97706]", light: "bg-[#FEF3C7]", text: "text-[#92400E]" };
  if (c.includes("U10") || c.includes("U11") || c.includes("U12") || c.includes("U13")) return { bg: "bg-[#1E3A8A]", light: "bg-blue-50", text: "text-[#1E3A8A]" };
  if (c.includes("U15") || c.includes("U17") || c.includes("U19")) return { bg: "bg-[#DC2626]", light: "bg-red-50", text: "text-[#DC2626]" };
  if (c.includes("GARDIEN")) return { bg: "bg-[#7c3aed]", light: "bg-purple-50", text: "text-[#7c3aed]" };
  if (c.includes("LOISIR") || c.includes("SENIOR")) return { bg: "bg-emerald-600", light: "bg-emerald-50", text: "text-emerald-700" };
  return { bg: "bg-slate-700", light: "bg-slate-100", text: "text-slate-700" };
}

export default function Trainings() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/trainings").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const byDay = useMemo(() => {
    const map = {};
    for (const d of DAYS) map[d.n] = [];
    for (const t of items) {
      if (map[t.day_of_week]) map[t.day_of_week].push(t);
    }
    for (const n of Object.keys(map)) {
      map[n].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    }
    return map;
  }, [items]);

  return (
    <div data-testid="trainings-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        <div className="absolute -top-20 -right-20 h-80 w-80 bg-[#1E3A8A]/40 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <span className="text-xs tracking-[0.3em] uppercase text-[#D97706] font-bold">Semaine type</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]" style={{ fontFamily: "Outfit" }}>
            Planning des <span className="text-[#D97706]">entraînements</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl leading-relaxed">
            Tous les créneaux hebdomadaires de chaque catégorie — du lundi au vendredi, au Stade de la Viabert. Les matchs se jouent samedi et dimanche.
          </p>
        </div>
      </section>

      {/* WEEKLY GRID (desktop) + STACK (mobile) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-6" data-testid="trainings-legend">
          {[
            { cat: "U6-U9", color: categoryColor("U7") },
            { cat: "U10-U13", color: categoryColor("U11") },
            { cat: "U15-U19", color: categoryColor("U17") },
            { cat: "Gardien", color: categoryColor("Gardien") },
            { cat: "Loisirs/Seniors", color: categoryColor("Loisir") },
          ].map((l) => (
            <div key={l.cat} className="flex items-center gap-2 text-xs text-[#475569]">
              <span className={`w-3 h-3 rounded-sm ${l.color.bg}`} />
              <span>{l.cat}</span>
            </div>
          ))}
        </div>

        {/* Desktop: 5-column grid */}
        <div className="hidden lg:grid grid-cols-5 gap-3" data-testid="trainings-grid-desktop">
          {DAYS.map((day) => (
            <div key={day.n} className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
              <div className="bg-[#1E3A8A] text-white p-3 text-center">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D97706] font-bold">{day.short}</div>
                <div className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{day.label}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {byDay[day.n].length === 0 && (
                  <div className="text-xs text-[#475569] italic text-center py-6">Repos</div>
                )}
                {byDay[day.n].map((t) => {
                  const c = categoryColor(t.category);
                  return (
                    <div key={t.id} className={`${c.light} rounded-md p-3 border-l-4 ${c.bg} border-l-current`} style={{ borderLeftColor: c.bg.replace("bg-[", "").replace("]", "").replace("bg-", "") }} data-testid={`training-${t.id}`}>
                      <div className={`text-[11px] font-bold uppercase tracking-wider ${c.text}`} style={{ fontFamily: "Outfit" }}>{t.category}</div>
                      <div className="text-xs text-[#0F172A] font-semibold mt-1 flex items-center gap-1">
                        <Clock size={11} /> {t.start_time} – {t.end_time}
                      </div>
                      {t.notes && <div className="text-[11px] text-[#475569] mt-1">{t.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: stacked list */}
        <div className="lg:hidden space-y-5" data-testid="trainings-list-mobile">
          {DAYS.map((day) => (
            <div key={day.n} className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
              <div className="bg-[#1E3A8A] text-white p-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D97706] font-bold">{day.short}</div>
                <div className="text-base font-bold" style={{ fontFamily: "Outfit" }}>{day.label}</div>
              </div>
              <div className="divide-y divide-[#E2E8F0]">
                {byDay[day.n].length === 0 && (
                  <div className="p-4 text-sm text-[#475569] italic">Pas d'entraînement</div>
                )}
                {byDay[day.n].map((t) => {
                  const c = categoryColor(t.category);
                  return (
                    <div key={t.id} className="p-4 flex items-start gap-3">
                      <div className={`w-1 self-stretch rounded ${c.bg} shrink-0`} />
                      <div className="flex-1">
                        <div className={`text-xs font-bold uppercase tracking-wider ${c.text}`} style={{ fontFamily: "Outfit" }}>{t.category}</div>
                        <div className="text-sm text-[#0F172A] font-semibold mt-1 flex items-center gap-1.5">
                          <Clock size={12} /> {t.start_time} – {t.end_time}
                        </div>
                        {t.notes && <div className="text-xs text-[#475569] mt-1">{t.notes}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Infos pratiques */}
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg border border-[#E2E8F0] bg-white p-5 flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-[#1E3A8A]/10 flex items-center justify-center text-[#1E3A8A] shrink-0">
              <MapPin size={18} />
            </div>
            <div>
              <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>Lieu des entraînements</div>
              <p className="text-sm text-[#475569] mt-1">Stade de la Viabert — 169 rue Anatole France, 69100 Villeurbanne</p>
            </div>
          </div>
          <div className="rounded-lg border border-[#E2E8F0] bg-white p-5 flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-[#D97706]/10 flex items-center justify-center text-[#D97706] shrink-0">
              <Dumbbell size={18} />
            </div>
            <div>
              <div className="font-bold text-[#0F172A]" style={{ fontFamily: "Outfit" }}>À prévoir</div>
              <p className="text-sm text-[#475569] mt-1">Tenue de sport, chaussures adaptées, <span className="font-bold text-[#DC2626]">protège-tibias obligatoires</span>, gourde d'eau.</p>
            </div>
          </div>
        </div>

        <div className="mt-5 p-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg flex items-start gap-3">
          <Info size={18} className="text-[#1E3A8A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#475569]">
            Les matchs se déroulent le <span className="font-bold text-[#0F172A]">samedi et dimanche</span>. Consultez le{" "}
            <Link to="/calendrier" className="text-[#1E3A8A] font-semibold hover:text-[#D97706] transition-colors">calendrier des matchs</Link>.
          </div>
        </div>
      </section>
    </div>
  );
}
